import { useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import getDatasetFromEvent from 'lib/analytics/getDatasetFromEvent'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import { promotionBanner } from 'analytics/promotions'
import { addGTMEvent } from 'api/googleTagManager'

const TileWrapper = styled.div`
  position: relative;
`

const TileLink = styled(ClickableLink)`
  display: block;
  overflow: hidden;
`

const Center = styled.div`
  padding: ${rem(10)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  pointer-events: none;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`

const Title = styled(Heading)`
  margin-top: auto;
  pointer-events: none;
`

const ShortText = styled(BodyText)`
  pointer-events: none;
  margin-top: ${rem(8)};
`

const LinkText = styled(BodyText)`
  margin-top: auto;
`

interface Props extends Omit<React.ComponentProps<typeof ResponsiveImage>, 'id'> {
  title: string
  shortText?: string
  linkTo?: string
  linkText: string
  href?: string
  image: string
  className?: string
}

function Tile(props: Props) {
  const intl = useIntl()
  const { title, shortText, linkTo, href, image, linkText, className, ...rest } = props
  const getPlacement = (bannerId: string) => [{
    bannerId,
    name: bannerId,
    position: 'homepage',
  }]

  const bannerClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(e => {
    const bannerId = getDatasetFromEvent(e, 'bannerid')
    const placements = getPlacement(bannerId)
    addGTMEvent(promotionBanner({ bannerId, placements, hitType: 'tile-click' }))
  }, [])

  return (
    <TileWrapper onClick={bannerClick} data-bannerid={`tile-${title}`} className={className}>
      <TileLink
        to={linkTo ? `/${linkTo}` : undefined}
        href={href}
      >
        <ResponsiveImage
          {...rest}
          brightness={-30}
          data-bannerid={`tile-${title}-image`}
          id={image}
          alt={title}
        />
      </TileLink>
      <Center>
        <Title variant="heading3" colour="dark-contrast" align="center" format="uppercase" forwardedAs="h2">{title}</Title>
        {shortText && <ShortText variant="medium" colour="dark-contrast" weight="bold" align="center" forwardedAs="h3">
          {shortText}
        </ShortText>}
        <LinkText variant="medium" colour="dark-contrast" underline align="center">{linkText ?? intl.formatMessage({ id: '-HiVl-Ww25rfu5R_kDOF', defaultMessage: 'Discover', description: 'Default link text for a guide tile when no custom text is provided' })}</LinkText>
      </Center>
    </TileWrapper>
  )
}

export default React.memo(Tile)
